import { useCallback, useMemo } from 'react';

import { useSession } from '@/features/auth/useSession';
import type { PermissionSlug } from './permissions';

export interface PermissionChecker {
  can: (permission: PermissionSlug) => boolean;
  canAny: (permissions: PermissionSlug[]) => boolean;
  canAll: (permissions: PermissionSlug[]) => boolean;
  permissions: string[];
}

/** Permission-aware rendering. Server-side policy remains the real gate. */
export function usePermissions(): PermissionChecker {
  const { session } = useSession();

  const permissions = useMemo(() => session?.permissions ?? [], [session]);
  const lookup = useMemo(() => new Set(permissions), [permissions]);

  const can = useCallback((permission: PermissionSlug) => lookup.has(permission), [lookup]);

  const canAny = useCallback(
    (candidates: PermissionSlug[]) => candidates.some((p) => lookup.has(p)),
    [lookup],
  );

  const canAll = useCallback(
    (candidates: PermissionSlug[]) => candidates.every((p) => lookup.has(p)),
    [lookup],
  );

  return { can, canAny, canAll, permissions };
}
